
import React, { useEffect } from 'react';
import { Alert, Title, Text } from '@gjensidige/builders-components';
import { Col, Row } from '@gjensidige/core-grid';
import {
  apiCallBegan,
  APICallMethods
} from '@gjensidige/se-redux-api-middleware';

import { useAppDispatch, useAppSelector } from '../hooks/appHooks';
import { ROUTES } from '../lib/constants';
import APP_CONSTANTS, { API_OPERATIONS, SESSION_KEYS } from '../lib/constants/appConstants';
import { PRODUCTS } from '../lib/constants/appConstants';
import API_ENDPOINTS from '../lib/constants/endpoints';
import {
  onApiError,
  onApiSuccess,
  updateLoadingStatus
} from '../store/slices/offerSlice';
import { fetchInsuranceOffers } from '../store/slices/offerSlice';
import { getSelectedPackages } from '../store/slices/offerSlice';
import { ErrorPageHandler } from '../components/Common/ErrorPage/ErrorPageHandler';
import { PageLoader } from '../components/Common/Loader/PageLoader';
import { SectionLoader } from '../components/Common/Loader/SectionLoader';
import { SectionHeader } from '../components/Common/SectionHeader/SectionHeader';
import { WelcomeBackModal } from '../components/Summary/WelcomeBackModal';
import { AdditionalPackagesContainer } from '../containers/AdditionalPackagesContainer';
import { EnquiryContainer } from '../containers/EnquiryContainer';
import { FooterContainer } from '../containers/FooterContainer';
import { HeaderContainer } from '../containers/HeaderContainer';
import { InsurancePackageContainer } from '../containers/InsurancePackageContainer';
import { SingleInsurancePackageContainer } from '../containers/SingleInsurancePackageContainer';
import { TotalPriceContainer } from '../containers/TotalPriceContainer';
import { IPricingStepConfig } from '../interfaces/EnquiryInterface';
import { Package } from '../interfaces/Types';
import { AppConstants } from '../utils/AppConstants';
import { ConfigHelper } from '../utils/ConfigHelper';
import { InsuranceHelper } from '../utils/InsuranceHelper';
import { RoutingHelper } from '../utils/RoutingHelper';
import { TextHelper } from '../utils/TextHelper';
import { InsuranceDocumentsContainer } from '../containers/InsuranceDocumentsContainer';

interface OfferPageProps {
  product: string;
}
export const OfferPage: React.FC<OfferPageProps> = (props: OfferPageProps) => {
  const dispatch = useAppDispatch();
  const isMetaDataReceived = useAppSelector(
    (state) => state.insurance.isMetaDataReceived
  );

  const { policyNumber: policyNumberFromUrl, channel: channelFromUrl } = InsuranceHelper.getPolicyAndChannelFromUrl();
  // Store the policy number in session storage for follow-up offers to distinguish between new and follow-up offers
  if (policyNumberFromUrl) {
    sessionStorage.setItem(
      SESSION_KEYS.FOLLOW_UP_OFFER,
      policyNumberFromUrl
    );
  }

  const insurancePackages = useAppSelector((state) =>
    state.insurance.packages.filter((item: Package) => !item.isPlusPackage)
  );

  const offerStepCode = RoutingHelper.getQuestionsStepCode(
    props.product,
    ROUTES.offerPage
  );

  const stepQuestions = useAppSelector((state) =>
    state.insurance.pricingQuestions.find(
      (item: IPricingStepConfig) => item.stepCode === offerStepCode
    )
  );

  const priliminaryQuestions = useAppSelector((state) =>
    state.insurance.pricingQuestions.find(
      (item: IPricingStepConfig) => item.stepCode === '000'
    )
  );

  const shouldShowPriliminaryQuestions = priliminaryQuestions?.questions.some(
    (question) => question.isVisible
  );

  const shouldShowQuestion = stepQuestions?.questions.some(
    (question) => question.isVisible
  );

  const selectedPackages = useAppSelector(getSelectedPackages);
  const selectedInsurancePackage = selectedPackages.find(
    (packageItem: Package) => !packageItem.isPlusPackage
  );

  const isUpdateOfferInProgress = useAppSelector(
    (state) => state.insurance.loading === 'updating'
  );

  const isCustomerEligible = useAppSelector((state) =>
    state.insurance.pricingQuestions.some((item: IPricingStepConfig) =>
      item.questions.some(
        (question) =>
          (question.APIKey === "workAbility") &&
          question.isVisible &&
          !question.isChecked
      )
    )
  );

  const isNiemiCustomer = channelFromUrl?.toLowerCase() === 'niemi';

  const isNextButtonDisabled =
    !selectedInsurancePackage?.isEnabled || isUpdateOfferInProgress || isCustomerEligible;

  const alertInfo = ConfigHelper.getAlertMessage(
    props.product,
    ROUTES.offerPage,
    InsuranceHelper.getChannelInfo().toLowerCase()
  );
  useEffect(() => {
    InsuranceHelper.setPageTitle(
      TextHelper.getPageTitle(props.product, ROUTES.offerPage)
    );
  }, []);

  useEffect(() => {
    if (isMetaDataReceived) {
      const policyStatus = InsuranceHelper.getPolicyCreationStatus(
        props.product,
        insurancePackages
      );
      if (!policyStatus.isPolicyCreated) {
        dispatch(updateLoadingStatus('pending'));
        dispatch(fetchInsuranceOffers(props.product));
      } else {
        if (policyStatus.isAllPackagesNotAvailable) {
          dispatch(updateLoadingStatus('pending'));
          if (policyStatus.policyNumber) {
            dispatch(
              apiCallBegan({
                init: {
                  headers: {
                    Applikasjon: APP_CONSTANTS.application
                  }
                },
                url: API_ENDPOINTS.getAllOffersURL(policyStatus.policyNumber),
                apiOperation: API_OPERATIONS.GET_ALL_OFFERS,
                method: APICallMethods.GET_JSON,
                onStart: '',
                onSuccess: onApiSuccess.type,
                onError: onApiError.type
              })
            );
          }
        }
      }
    }
  }, [isMetaDataReceived]);

  return (
    <PageLoader>
      <ErrorPageHandler>
        <HeaderContainer currentPage="offer" productName={props.product} />
        {insurancePackages.length > 0 && (
          <>
            <main>
              <div className="mb-6">
                <TotalPriceContainer stickToTop />
                <SectionLoader></SectionLoader>
                {(alertInfo.title || alertInfo.message) && (
                  <Row className="mt-md-5 mt-3">
                    <Col
                      sm="12"
                      md={{ span: 8, offset: 2 }}
                      lg={{ span: 8, offset: 2 }}
                      xl={{ span: 6, offset: 3 }}
                    >
                      <Alert
                        className="alert-info-message"
                        severity="success"
                      >
                        {
                          alertInfo.title && (
                            <Title>{alertInfo.title}</Title>
                          )
                        }
                        {
                          alertInfo.message && (
                            <Text>{alertInfo.message}</Text>
                          )
                        }
                      </Alert>
                    </Col>
                  </Row>
                )}
                {
                  InsuranceHelper.isSingleCoverageProduct(props.product) ?
                    <Row className="mt-5 single-package-container">
                      <SingleInsurancePackageContainer />
                    </Row>
                    :
                    <>
                      <Row>
                        <SectionHeader
                          wrapperClass={`align-center-lg full-width pt-4 ${alertInfo.title || alertInfo.message ? '' : 'pt-md-4'
                            } offer-header-label ${!shouldShowPriliminaryQuestions ? 'mb-4' : ''}`}
                          headerText={AppConstants.getAppText(
                            'webshop.insuranceSelectionHeader'
                          )}
                        />
                      </Row>

                      {props.product.toLowerCase() === PRODUCTS.VILLA && (
                        <Row className="mt-3 mt-md-5">
                          <Col
                            sm="12"
                            md={{ span: 8, offset: 2 }}
                            lg={{ span: 8, offset: 2 }}
                            xl={{ span: 6, offset: 3 }}
                          >
                            <Alert
                              className="alert-info-message"
                              severity="informative"
                              variant="outlined"
                            >

                              <Title>
                                {AppConstants.getAppText(
                                  'webshop.villa.offerMessageTitle'
                                )}
                              </Title>
                              <Text size="body">
                                {AppConstants.getAppText(
                                  'webshop.villa.offerMessageDescription'
                                )}
                              </Text>
                            </Alert>
                          </Col>
                        </Row>
                      )}
                      {shouldShowPriliminaryQuestions && (
                        <Row>
                          <Col
                            md={{ span: 8, offset: 2 }}
                            lg={{ span: 8, offset: 2 }}
                            xl={{ span: 6, offset: 3 }}
                            sm="12"
                          >
                            <div className="priliminary-questions-wrapper">
                              <EnquiryContainer stepCode="000" isVerticalGroup wrapperClassName="center-aligned" />
                            </div>
                          </Col>
                        </Row>
                      )}
                      <Row className="pt-2 pt-md-3">
                        <InsurancePackageContainer />
                      </Row>

                      {InsuranceHelper.isAddOnsAvailableForProductType(props.product) && (
                        <Row className="mt-4">
                          <Col
                            md={{ span: 8, offset: 2 }}
                            lg={{ span: 8, offset: 2 }}
                            xl={{ span: 6, offset: 3 }}
                            sm="12"
                          >
                            <AdditionalPackagesContainer />
                          </Col>
                        </Row>
                      )}

                      {shouldShowQuestion && (
                        <Row className="mt-4 mb-5">
                          <Col
                            md={{ span: 8, offset: 2 }}
                            lg={{ span: 8, offset: 2 }}
                            xl={{ span: 6, offset: 3 }}
                            sm="12"
                            className="card-wrapper"
                          >
                            <EnquiryContainer stepCode={offerStepCode} isVerticalGroup wrapperClassName="full-width" />
                          </Col>
                        </Row>
                      )}

                      <Row className="mt-4">
                        <Col
                          md={{ span: 8, offset: 2 }}
                          lg={{ span: 8, offset: 2 }}
                          xl={{ span: 6, offset: 3 }}
                          sm="12"
                        >
                          <InsuranceDocumentsContainer displayAsExpandable={true} />
                        </Col>
                      </Row>
                    </>
                }

                {selectedInsurancePackage?.packageName && (
                  <div className="mt-5">
                    <TotalPriceContainer />
                  </div>
                )}
                <Row>
                  <Col
                    sm="12"
                    md={{ span: 8, offset: 2 }}
                    lg={{ span: 8, offset: 2 }}
                    xl={{ span: 6, offset: 3 }}
                    className="pt-5 footer-section-wrapper"
                  >
                    <FooterContainer
                      isNextButtonDisabled={isNextButtonDisabled}
                      shouldDisplayGoBackButton={ConfigHelper.isBackButtonEnabledForOfferPage(
                        props.product
                      )}
                    />
                  </Col>
                </Row>
              </div>
            </main>
            {isNiemiCustomer && (
              <WelcomeBackModal
                welcomeMessage={{
                  header: 'Kontrollera dina uppgifter',
                  message: 'Vad roligt att du vill teckna en försäkring! Vi har fyllt i de uppgifter vi har fått angående din bil och körning. Du har fått samma omfattning som du hade med prova på-försäkringen. Du kan ändra valen så det passar ditt behov.'
                }}
                primaryBtnLabel="Jag förstår"
                closeLabel="Stäng information"
                modalLabel="Hämtade uppgifter"
              />
            )}
          </>
        )}
      </ErrorPageHandler>
    </PageLoader>
  );
};
